export const LETTER_TEMPLATE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export const LETTER_TEMPLATE_IMAGE_ALLOWED_MIME_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
]);

/**
 * @typedef {{ operationId: string, file: File, language: 'fr' | 'en' }} ImageUploadAttempt
 */

/**
 * @param {File} file
 * @returns {'invalid_mime' | 'too_large' | null}
 */
export function validateLetterTemplateImageFile(file) {
  if (!file || typeof file !== 'object') return 'invalid_mime';
  if (!LETTER_TEMPLATE_IMAGE_ALLOWED_MIME_TYPES.includes(file.type)) return 'invalid_mime';
  if (typeof file.size !== 'number' || file.size > LETTER_TEMPLATE_IMAGE_MAX_BYTES) return 'too_large';
  return null;
}

/**
 * @param {File} file
 * @param {'fr' | 'en'} language
 * @param {string} operationId
 * @returns {ImageUploadAttempt}
 */
export function createImageUploadAttempt(file, language, operationId) {
  return {
    operationId: String(operationId ?? '').trim(),
    file,
    language,
  };
}

/**
 * @param {ImageUploadAttempt | null | undefined} attempt
 * @param {File} file
 */
export function isSameImageUploadAttemptFile(attempt, file) {
  return Boolean(attempt && attempt.file === file);
}

/**
 * @param {ImageUploadAttempt | null | undefined} failedAttempt
 * @param {File} file
 * @param {'fr' | 'en'} language
 * @param {() => string} generateOperationId
 */
export function resolveImageUploadAttempt(failedAttempt, file, language, generateOperationId) {
  if (failedAttempt && isSameImageUploadAttemptFile(failedAttempt, file) && failedAttempt.language === language) {
    return failedAttempt;
  }
  return createImageUploadAttempt(file, language, generateOperationId());
}

/**
 * Convert a File to raw base64 without stack-overflowing on large files.
 * @param {File} file
 * @returns {Promise<string>}
 */
export function fileToRawBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('invalid_file_reader_result'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('file_read_failed'));
    reader.readAsDataURL(file);
  });
}
